import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, OverlayTrigger, Spinner, Table, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import Footer from "../../components/comunes/Footer";
import TituloPagina from "../../components/comunes/TituloPagina";
import { DarkModeContext } from "../../context/DarkModeContext";
import { getToken } from "../../utils/auth";
import { canManageOperacionesImportacion, getRoleFromToken } from "../../utils/roleAccess";
import { importacionOperacionesApi } from "./importacionOperacionesApi";
import "./importacion-operaciones.css";

const FILTER_OPTIONS = [
  { value: "TODAS", label: "Todas" },
  { value: "ERROR", label: "Invalidas" },
  { value: "WARNING", label: "Con advertencias" },
  { value: "VALIDA", label: "Validas" },
];

const SUMMARY_ITEMS = [
  { key: "totalFilas", label: "Filas analizadas" },
  { key: "filasValidas", label: "Filas validas" },
  { key: "filasInvalidas", label: "Filas invalidas" },
  { key: "filasConAdvertencias", label: "Filas con advertencias" },
  { key: "cantidadErroresCampo", label: "Errores de campo" },
  { key: "cantidadAdvertenciasCampo", label: "Advertencias de campo" },
];

const getSeverityVariant = (severity) => {
  if (severity === "ERROR") return "danger";
  if (severity === "WARNING") return "warning";
  return "secondary";
};

const getRowVariant = (estado) => {
  if (estado === "ERROR") return "danger";
  if (estado === "WARNING") return "warning";
  return "success";
};

const getImportStatusVariant = (estado) => {
  if (estado === "BLOQUEADA" || estado === "FALLIDA" || estado === "BLOQUEADA_DUPLICADOS") return "danger";
  if (estado === "LISTA_PARA_CONFIRMAR") return "warning";
  if (estado === "CONFIRMANDO") return "info";
  if (estado === "COMPLETADA" || estado === "COMPLETADA_CON_RECHAZOS" || estado === "CONFIRMADA") return "success";
  return "secondary";
};

const normalizeHistorialPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const getConfirmDisabledReason = (item) => {
  if (!item) {
    return "Selecciona una importacion guardada para confirmar.";
  }

  if (!item?.confirmacionRealHabilitada) {
    return "La confirmacion real esta deshabilitada por configuracion.";
  }

  if (item?.estado !== "LISTA_PARA_CONFIRMAR") {
    return "La importacion no esta lista para confirmar.";
  }

  if ((item?.filasInvalidas ?? 0) > 0) {
    return "La importacion tiene filas invalidas y no puede confirmarse.";
  }

  if (item?.yaFueConfirmada) {
    return "La importacion ya fue confirmada.";
  }

  return "";
};

const canConfirmImport = (item, confirmando) => {
  if (!item) return false;

  return Boolean(item.confirmacionRealHabilitada)
    && item.estado === "LISTA_PARA_CONFIRMAR"
    && (item.filasInvalidas ?? 0) === 0
    && !item.yaFueConfirmada
    && Boolean(item.puedeConfirmarse)
    && !confirmando;
};

const matchesFilter = (row, filter) => filter === "TODAS" || row.estado === filter || row.estadoValidacion === filter;
const formatValue = (value) => value || "-";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("es-AR");
};

const formatCurrency = (value) => {
  const numericValue = Number(value || 0);
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
  }).format(Number.isFinite(numericValue) ? numericValue : 0);
};

const IssueTable = ({ title, items, emptyMessage }) => (
  <div className="mt-3">
    <h6 className="mb-2">{title}</h6>
    <div className="importacion-operaciones-table-wrap">
      <Table responsive hover size="sm" className="mb-0">
        <thead>
          <tr>
            <th>Columna</th>
            <th>Valor original</th>
            <th>Codigo</th>
            <th>Mensaje</th>
          </tr>
        </thead>
        <tbody>
          {items.length ? items.map((item, index) => (
            <tr key={`${title}-${item.numeroFila}-${item.codigo}-${index}`}>
              <td>{item.columna}</td>
              <td>{formatValue(item.valorOriginal)}</td>
              <td>{item.codigo}</td>
              <td>{item.mensaje}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan="4" className="importacion-operaciones-empty">{emptyMessage}</td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  </div>
);

const ImportacionOperaciones = () => {
  const navigate = useNavigate();
  const { darkMode } = React.useContext(DarkModeContext);
  const [tipoArchivo, setTipoArchivo] = useState("SAS");
  const [proveedor, setProveedor] = useState("FISERV");
  const [fechaInformada, setFechaInformada] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [importacionGuardada, setImportacionGuardada] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [historialError, setHistorialError] = useState("");
  const [cargandoAnalisis, setCargandoAnalisis] = useState(false);
  const [guardandoAnalisis, setGuardandoAnalisis] = useState(false);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [cargandoPreconfirmacion, setCargandoPreconfirmacion] = useState(false);
  const [preconfirmacion, setPreconfirmacion] = useState(null);
  const [confirmacionPendienteId, setConfirmacionPendienteId] = useState(null);
  const [rowFilter, setRowFilter] = useState("TODAS");
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);

  const token = getToken();
  const role = useMemo(() => getRoleFromToken(token), [token]);

  useEffect(() => {
    if (!token || !canManageOperacionesImportacion(role)) {
      navigate("/");
    }
  }, [navigate, role, token]);

  useEffect(() => {
    if (token && canManageOperacionesImportacion(role)) {
      loadHistorial();
    }
  }, [token, role]);

  const filas = useMemo(() => Array.isArray(resultado?.filas) ? resultado.filas : [], [resultado?.filas]);
  const filteredRows = useMemo(() => filas.filter((row) => matchesFilter(row, rowFilter)), [filas, rowFilter]);
  const resumenPorCodigo = useMemo(
    () => Array.isArray(resultado?.resumenPorCodigo) ? resultado.resumenPorCodigo : [],
    [resultado?.resumenPorCodigo]
  );
  const puedeGuardar = Boolean(archivo) && !cargandoAnalisis && !guardandoAnalisis;
  const puedeConfirmarActual = Boolean(importacionGuardada?.id) && canConfirmImport(importacionGuardada, confirmando);
  const mensajeConfirmacionDeshabilitadaActual = getConfirmDisabledReason(importacionGuardada)
    || "La confirmacion real esta deshabilitada por configuracion o la importacion no cumple las validaciones previas.";
  const mensajeConfiguracionConfirmacion = importacionGuardada?.confirmacionRealHabilitada
    ? "La confirmacion real depende de las validaciones previas de la importacion y de la configuracion activa del backend para el destino seleccionado."
    : "La confirmacion real depende de la configuracion activa del backend y de las validaciones previas antes de insertar en base_dashboard.";

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("archivo", archivo);
    formData.append("tipoArchivo", tipoArchivo);
    formData.append("proveedor", proveedor);
    formData.append("dryRun", "true");
    formData.append("token", token || "");
    if (fechaInformada) {
      formData.append("fechaInformada", fechaInformada);
    }

    return formData;
  };

  const loadHistorial = async () => {
    if (!token) {
      const errorMessage = "No hay token disponible.";
      setHistorial([]);
      setHistorialError(errorMessage);
      Swal.fire("Importacion", errorMessage, "error");
      return;
    }

    try {
      setCargandoHistorial(true);
      setHistorialError("");
      const data = await importacionOperacionesApi.obtenerHistorial(token);
      setHistorial(normalizeHistorialPayload(data));
      setHistorialError("");
    } catch (error) {
      const errorMessage = error.message || "No se pudo cargar el historial";
      setHistorial([]);
      setHistorialError(errorMessage);
      Swal.fire("Importacion", "No se pudo cargar el historial", "error");
    } finally {
      setCargandoHistorial(false);
    }
  };

  const loadImportacion = async (id) => {
    try {
      setCargandoAnalisis(true);
      const [detalle, filasGuardadas] = await Promise.all([
        importacionOperacionesApi.obtenerImportacion(id, token),
        importacionOperacionesApi.obtenerFilas(id, token),
      ]);

      setImportacionGuardada(detalle);
      setResultado({
        nombreArchivo: detalle.nombreArchivo,
        tipoArchivo: detalle.tipoArchivo,
        proveedor: detalle.proveedor,
        hashSha256: detalle.hashSha256,
        totalFilas: detalle.totalFilas,
        filasValidas: detalle.filasValidas,
        filasInvalidas: detalle.filasInvalidas,
        filasConAdvertencias: detalle.filasConAdvertencias,
        cantidadErroresCampo: detalle.erroresCampo,
        cantidadAdvertenciasCampo: detalle.advertenciasCampo,
        duplicadosInternos: detalle.filasDuplicadas,
        dryRun: true,
        puedeConfirmarse: detalle.puedeConfirmarse,
        bloqueadaPorErrores: detalle.filasInvalidas > 0,
        motivosBloqueo: detalle.motivosBloqueo || [],
        filas: (filasGuardadas || []).map((row) => ({
          numeroFila: row.numeroFila,
          estado: row.estadoValidacion,
          cantidadErrores: row.errores?.length || 0,
          cantidadAdvertencias: row.advertencias?.length || 0,
          resumen: row.resumen,
          errores: row.errores || [],
          advertencias: row.advertencias || [],
        })),
        resumenPorCodigo: [],
      });
    } catch (error) {
      Swal.fire("Importacion", error.message || "No se pudo cargar la importacion.", "error");
    } finally {
      setCargandoAnalisis(false);
    }
  };

  const handleAnalizar = async (event) => {
    event.preventDefault();
    if (!archivo) {
      Swal.fire("Importacion", "Selecciona un archivo Excel para analizar.", "info");
      return;
    }

    try {
      setCargandoAnalisis(true);
      setFilaSeleccionada(null);
      setImportacionGuardada(null);
      const data = await importacionOperacionesApi.analizar(buildFormData(), token);
      setResultado(data);
    } catch (error) {
      Swal.fire("Importacion", error.message || "No se pudo analizar el archivo.", "error");
    } finally {
      setCargandoAnalisis(false);
    }
  };

  const handleGuardarAnalisis = async () => {
    if (!archivo) {
      Swal.fire("Importacion", "Selecciona un archivo Excel para guardar el analisis.", "info");
      return;
    }

    try {
      setGuardandoAnalisis(true);
      const data = await importacionOperacionesApi.analizarYGuardar(buildFormData(), token);
      const detalle = await importacionOperacionesApi.obtenerImportacion(data.importacionId, token);
      setResultado({
        ...data.analisis,
        puedeConfirmarse: detalle.puedeConfirmarse,
      });
      setImportacionGuardada(detalle);
      await loadHistorial();
      Swal.fire("Importacion", data.mensaje || "El analisis se guardo en staging.", "success");
    } catch (error) {
      Swal.fire("Importacion", error.message || "No se pudo guardar el analisis.", "error");
    } finally {
      setGuardandoAnalisis(false);
    }
  };

  const handleSolicitarConfirmacion = async (id) => {
    try {
      setCargandoPreconfirmacion(true);
      const resumen = await importacionOperacionesApi.obtenerPreconfirmacion(id, token);
      setConfirmacionPendienteId(id);
      setPreconfirmacion(resumen);
    } catch (error) {
      Swal.fire("Importacion", error.message || "No se pudo preparar la preconfirmacion.", "error");
    } finally {
      setCargandoPreconfirmacion(false);
    }
  };

  const handleCerrarPreconfirmacion = () => {
    if (confirmando) return;
    setPreconfirmacion(null);
    setConfirmacionPendienteId(null);
  };

  const handleConfirmar = async () => {
    if (!confirmacionPendienteId) return;
    const importacionId = confirmacionPendienteId;

    try {
      setConfirmando(true);
      const data = await importacionOperacionesApi.confirmar(importacionId, token, { confirmacionUsuario: true });
      handleCerrarPreconfirmacion();
      Swal.fire("Importacion", data.mensaje || "La importacion fue confirmada.", "success");
      await loadHistorial();
      await loadImportacion(importacionId);
    } catch (error) {
      Swal.fire("Importacion", error.message || "No se pudo confirmar la importacion.", "error");
    } finally {
      setConfirmando(false);
    }
  };

  return (
    <section className={darkMode ? "container-dark importacion-operaciones-page" : "container-light bg-gris importacion-operaciones-page"}>
      <div className="container py-4">
        <TituloPagina title="Importacion de Operaciones" />

        <Alert variant="info" className="mt-4">
          {mensajeConfiguracionConfirmacion}
        </Alert>

        <Card className="importacion-operaciones-card mt-3">
          <Card.Body>
            <Form onSubmit={handleAnalizar}>
              <div className="row g-3">
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Tipo de archivo</Form.Label>
                    <Form.Select value={tipoArchivo} onChange={(event) => setTipoArchivo(event.target.value)}>
                      <option value="SAS">SAS</option>
                      <option value="QR">QR</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Proveedor</Form.Label>
                    <Form.Control value={proveedor} onChange={(event) => setProveedor(event.target.value)} placeholder="FISERV" />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Fecha informada</Form.Label>
                    <Form.Control type="date" value={fechaInformada} onChange={(event) => setFechaInformada(event.target.value)} />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group>
                    <Form.Label>Archivo</Form.Label>
                    <Form.Control type="file" accept=".xlsx" onChange={(event) => setArchivo(event.target.files?.[0] || null)} />
                  </Form.Group>
                </div>
              </div>

              <div className="d-flex gap-2 flex-wrap mt-4">
                <Button type="submit" variant="success" disabled={!puedeGuardar}>
                  {cargandoAnalisis ? <><Spinner animation="border" size="sm" className="me-2" />Analizando...</> : "Analizar archivo"}
                </Button>
                <Button type="button" variant="outline-success" disabled={!puedeGuardar} onClick={handleGuardarAnalisis}>
                  {guardandoAnalisis ? <><Spinner animation="border" size="sm" className="me-2" />Guardando...</> : "Guardar analisis"}
                </Button>
                <OverlayTrigger overlay={<Tooltip id="tooltip-confirmar">{mensajeConfirmacionDeshabilitadaActual}</Tooltip>}>
                  <span className="d-inline-block" title={puedeConfirmarActual ? "" : mensajeConfirmacionDeshabilitadaActual}>
                    <Button type="button" variant="outline-secondary" disabled={!puedeConfirmarActual || cargandoPreconfirmacion} onClick={() => handleSolicitarConfirmacion(importacionGuardada.id)}>
                      {cargandoPreconfirmacion ? "Preparando..." : "Confirmar carga"}
                    </Button>
                  </span>
                </OverlayTrigger>
              </div>
            </Form>
          </Card.Body>
        </Card>

        {resultado ? (
          <>
            <div className="importacion-operaciones-grid mt-4">
              {SUMMARY_ITEMS.map((item) => (
                <Card key={item.key} className="importacion-operaciones-card">
                  <Card.Body>
                    <div className="importacion-operaciones-kpi-label">{item.label}</div>
                    <div className="importacion-operaciones-kpi-value">{resultado[item.key] ?? 0}</div>
                  </Card.Body>
                </Card>
              ))}
            </div>

            <Card className="importacion-operaciones-card mt-4">
              <Card.Body>
                <div className="importacion-operaciones-toolbar">
                  <div>
                    <strong>{resultado.nombreArchivo}</strong>
                    <div className="text-muted small">
                      Tipo: {resultado.tipoArchivo} | Proveedor: {resultado.proveedor} | Hash SHA-256: {resultado.hashSha256}
                    </div>
                    {importacionGuardada ? (
                      <div className="text-muted small mt-1">
                        Importacion #{importacionGuardada.id} | Estado staging: {importacionGuardada.estado}
                      </div>
                    ) : null}
                  </div>
                  <Badge bg={resultado.bloqueadaPorErrores ? "danger" : "secondary"} className="importacion-operaciones-badge">
                    {resultado.bloqueadaPorErrores ? "Archivo bloqueado por errores" : "Analisis sin bloqueo"}
                  </Badge>
                </div>

                <div className="importacion-operaciones-meta mt-3">
                  <span><strong>Duplicados internos:</strong> {resultado.duplicadosInternos ?? 0}</span>
                  <span><strong>DryRun:</strong> {resultado.dryRun ? "Si" : "No"}</span>
                  <span><strong>Puede confirmarse:</strong> {resultado.puedeConfirmarse ? "Si" : "No"}</span>
                </div>

                {!importacionGuardada?.confirmacionRealHabilitada ? (
                  <Alert variant="warning" className="mt-3 mb-0">
                    La confirmacion real todavia no esta habilitada.
                  </Alert>
                ) : null}

                {resultado.motivosBloqueo?.length ? (
                  <Alert variant="danger" className="mt-3 mb-0">
                    <div className="fw-semibold mb-2">Motivos de bloqueo</div>
                    <ul className="mb-0 importacion-operaciones-list">
                      {resultado.motivosBloqueo.map((motivo, index) => (
                        <li key={`${motivo}-${index}`}>{motivo}</li>
                      ))}
                    </ul>
                  </Alert>
                ) : null}
              </Card.Body>
            </Card>

            <Card className="importacion-operaciones-card mt-4">
              <Card.Body>
                <div className="importacion-operaciones-toolbar">
                  <h5 className="mb-0">Filas analizadas</h5>
                  <Form.Select style={{ maxWidth: 240 }} value={rowFilter} onChange={(event) => setRowFilter(event.target.value)}>
                    {FILTER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </div>

                <div className="importacion-operaciones-table-wrap mt-3">
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Fila</th>
                        <th>Estado</th>
                        <th>Errores</th>
                        <th>Advertencias</th>
                        <th>Resumen</th>
                        <th className="text-end">Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.length ? filteredRows.map((row) => (
                        <tr key={`fila-${row.numeroFila}`}>
                          <td>{row.numeroFila}</td>
                          <td>
                            <Badge bg={getRowVariant(row.estado || row.estadoValidacion)}>{row.estado || row.estadoValidacion}</Badge>
                          </td>
                          <td>{row.cantidadErrores}</td>
                          <td>{row.cantidadAdvertencias}</td>
                          <td>{row.resumen}</td>
                          <td className="text-end">
                            <Button size="sm" variant="outline-primary" onClick={() => setFilaSeleccionada(row)}>
                              Ver detalle
                            </Button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="importacion-operaciones-empty">No hay filas para el filtro seleccionado.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            <Card className="importacion-operaciones-card mt-4 mb-4">
              <Card.Body>
                <h5>Resumen por codigo</h5>
                <div className="importacion-operaciones-table-wrap mt-3">
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Codigo</th>
                        <th>Severidad</th>
                        <th>Filas afectadas</th>
                        <th>Campos afectados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumenPorCodigo.length ? resumenPorCodigo.map((item, index) => (
                        <tr key={`${item.codigo}-${item.severidad}-${index}`}>
                          <td>{item.codigo}</td>
                          <td><Badge bg={getSeverityVariant(item.severidad)}>{item.severidad}</Badge></td>
                          <td>{item.cantidadFilas}</td>
                          <td>{item.cantidadCampos}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="4" className="importacion-operaciones-empty">No se detectaron incidencias agregadas para esta vista.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </>
        ) : null}

        <Card className="importacion-operaciones-card mt-4 mb-5">
          <Card.Body>
            <div className="importacion-operaciones-toolbar">
              <h5 className="mb-0">Historial de importaciones</h5>
              <Button type="button" variant="outline-primary" size="sm" onClick={loadHistorial} disabled={cargandoHistorial}>
                {cargandoHistorial ? "Actualizando..." : "Actualizar"}
              </Button>
            </div>

            <div className="importacion-operaciones-table-wrap mt-3">
              <Table responsive hover className="importacion-operaciones-history-table">
                <thead>
                  <tr>
                    <th className="importacion-operaciones-col-fecha">Fecha</th>
                    <th className="importacion-operaciones-col-archivo">Archivo</th>
                    <th className="importacion-operaciones-col-tipo">Tipo</th>
                    <th className="importacion-operaciones-col-proveedor">Proveedor</th>
                    <th className="importacion-operaciones-col-estado">Estado</th>
                    <th className="importacion-operaciones-col-number importacion-operaciones-col-total">Total</th>
                    <th className="importacion-operaciones-col-number">Validas</th>
                    <th className="importacion-operaciones-col-number">Invalidas</th>
                    <th className="importacion-operaciones-col-number">Insertadas</th>
                    <th className="importacion-operaciones-col-number">Duplicadas</th>
                    <th className="importacion-operaciones-col-usuario">Usuario</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.length ? historial.map((item) => (
                    <tr key={`historial-${item.id}`} className="importacion-operaciones-history-row">
                      <td className="importacion-operaciones-col-fecha">{formatDateTime(item.fechaCreacion)}</td>
                      <td className="importacion-operaciones-col-archivo">{item.nombreArchivo}</td>
                      <td className="importacion-operaciones-col-tipo">{item.tipoArchivo}</td>
                      <td className="importacion-operaciones-col-proveedor">{item.proveedor}</td>
                      <td className="importacion-operaciones-col-estado">
                        <Badge bg={getImportStatusVariant(item.estado)} className="importacion-operaciones-history-badge">{item.estado}</Badge>
                      </td>
                      <td className="importacion-operaciones-col-number importacion-operaciones-col-total">{item.totalFilas}</td>
                      <td className="importacion-operaciones-col-number">{item.filasValidas}</td>
                      <td className="importacion-operaciones-col-number">{item.filasInvalidas}</td>
                      <td className="importacion-operaciones-col-number">{item.filasInsertadas}</td>
                      <td className="importacion-operaciones-col-number">{item.filasDuplicadas}</td>
                      <td className="importacion-operaciones-col-usuario">{item.usuarioNombre}</td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end flex-wrap">
                          <Button size="sm" variant="outline-primary" onClick={() => loadImportacion(item.id)}>
                            Ver detalle
                          </Button>
                          <a className="btn btn-sm btn-outline-secondary" href={importacionOperacionesApi.buildDownloadUrl(item.id, "invalidas", token)}>
                            Descargar rechazadas
                          </a>
                          <OverlayTrigger overlay={<Tooltip id={`tooltip-historial-${item.id}`}>{getConfirmDisabledReason(item) || "La importacion puede confirmarse."}</Tooltip>}>
                            <span
                              className="d-inline-block"
                              title={canConfirmImport(item, confirmando) ? "" : getConfirmDisabledReason(item)}
                            >
                              <Button
                                size="sm"
                                variant="outline-success"
                                disabled={!canConfirmImport(item, confirmando) || cargandoPreconfirmacion}
                                onClick={() => handleSolicitarConfirmacion(item.id)}
                              >
                                {cargandoPreconfirmacion && confirmacionPendienteId === item.id ? "Preparando..." : "Confirmar"}
                              </Button>
                            </span>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="12" className="importacion-operaciones-empty">
                        {cargandoHistorial
                          ? "Cargando historial..."
                          : historialError || "Todavia no hay importaciones guardadas."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Modal show={Boolean(filaSeleccionada)} onHide={() => setFilaSeleccionada(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Fila {filaSeleccionada?.numeroFila} - {filaSeleccionada?.estado || filaSeleccionada?.estadoValidacion || "SIN_ESTADO"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {filaSeleccionada ? (
            <>
              <div className="importacion-operaciones-detail-grid">
                <div className="importacion-operaciones-detail-box">
                  <div className="importacion-operaciones-kpi-label">Errores</div>
                  <div className="importacion-operaciones-kpi-value">{filaSeleccionada.cantidadErrores}</div>
                </div>
                <div className="importacion-operaciones-detail-box">
                  <div className="importacion-operaciones-kpi-label">Advertencias</div>
                  <div className="importacion-operaciones-kpi-value">{filaSeleccionada.cantidadAdvertencias}</div>
                </div>
              </div>
              <p className="mt-3 mb-0"><strong>Resumen:</strong> {filaSeleccionada.resumen}</p>
              <IssueTable
                title="Errores"
                items={filaSeleccionada.errores || []}
                emptyMessage="La fila no tiene errores."
              />
              <IssueTable
                title="Advertencias"
                items={filaSeleccionada.advertencias || []}
                emptyMessage="La fila no tiene advertencias."
              />
            </>
          ) : null}
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(preconfirmacion)} onHide={handleCerrarPreconfirmacion} centered>
        <Modal.Header closeButton={!confirmando}>
          <Modal.Title>Preconfirmacion de importacion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {preconfirmacion ? (
            <>
              <div className="importacion-operaciones-detail-grid">
                <div className="importacion-operaciones-detail-box">
                  <div className="importacion-operaciones-kpi-label">Total</div>
                  <div className="importacion-operaciones-kpi-value">{preconfirmacion.total ?? 0}</div>
                </div>
                <div className="importacion-operaciones-detail-box">
                  <div className="importacion-operaciones-kpi-label">Nuevas</div>
                  <div className="importacion-operaciones-kpi-value">{preconfirmacion.nuevas ?? 0}</div>
                </div>
                <div className="importacion-operaciones-detail-box">
                  <div className="importacion-operaciones-kpi-label">Duplicados exactos</div>
                  <div className="importacion-operaciones-kpi-value">{preconfirmacion.duplicadosExactos ?? 0}</div>
                </div>
                <div className="importacion-operaciones-detail-box">
                  <div className="importacion-operaciones-kpi-label">Posibles duplicados</div>
                  <div className="importacion-operaciones-kpi-value">{preconfirmacion.posiblesDuplicados ?? 0}</div>
                </div>
              </div>

              <div className="mt-3">
                <div><strong>Importe bruto total:</strong> {formatCurrency(preconfirmacion.importeBrutoTotal)}</div>
                <div><strong>Importe bruto a insertar:</strong> {formatCurrency(preconfirmacion.importeBrutoInsertado)}</div>
                <div><strong>Estado actual:</strong> {preconfirmacion.estado}</div>
                <div><strong>Datos aptos para confirmar:</strong> {preconfirmacion.datosAptosParaConfirmar ? "Si" : "No"}</div>
                <div><strong>Confirmacion real habilitada:</strong> {preconfirmacion.confirmacionRealHabilitada ? "Si" : "No"}</div>
                <div><strong>Puede confirmarse ahora:</strong> {preconfirmacion.puedeConfirmarse ? "Si" : "No"}</div>
              </div>

              {preconfirmacion.compatibilidadesNormalizadas?.length ? (
                <Alert variant="info" className="mt-3 mb-0">
                  <div className="fw-semibold mb-2">Compatibilidad normalizada destacada</div>
                  <ul className="mb-0 importacion-operaciones-list">
                    {preconfirmacion.compatibilidadesNormalizadas.map((item, index) => (
                      <li key={`${item.campo}-${index}`}>
                        {item.campo}: origen {item.valorOrigen}, legacy {item.valorLegacyFloat ?? "N/D"}, normalizado {item.valorNormalizadoDecimal ?? "N/D"}.
                      </li>
                    ))}
                  </ul>
                </Alert>
              ) : null}

              {preconfirmacion.motivosBloqueo?.length ? (
                <Alert variant="warning" className="mt-3 mb-0">
                  <div className="fw-semibold mb-2">Bloqueos detectados</div>
                  <ul className="mb-0 importacion-operaciones-list">
                    {preconfirmacion.motivosBloqueo.map((motivo, index) => (
                      <li key={`${motivo}-${index}`}>{motivo}</li>
                    ))}
                  </ul>
                </Alert>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCerrarPreconfirmacion} disabled={confirmando}>
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmar}
            disabled={!preconfirmacion?.puedeConfirmarse || confirmando}
          >
            {confirmando ? "Confirmando..." : "Confirmar ahora"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </section>
  );
};

export default ImportacionOperaciones;
